/**
 * Guardián de la sección de administración.
 *
 * SE APOYA EN `Protegida` EN VEZ DE REPETIRLA. Los cuatro desenlaces de sesión
 * —comprobando, fallo, sin OAuth configurado, sin registro— ya están resueltos
 * ahí, con sus textos y sus salidas. Duplicarlos aquí garantizaría que algún día
 * divergieran, y una divergencia en un guardián se ve como una pantalla rota.
 * Lo único que este componente agrega es la comprobación de PERMISO.
 *
 * PREGUNTA POR PERMISOS, NO POR EL ROL (ADR-0007). Antes comparaba contra la
 * cadena `administrador`. Con los roles convertidos en datos y editables desde
 * la propia aplicación, esa comparación tenía un fallo concreto: un rol nuevo
 * —«coordinacion», con permiso para bloquear espacios— habría recibido un 200
 * del API y una pantalla de «no puedes entrar aquí».
 *
 * ENTRA QUIEN PUEDA HACER ALGUNA DE LAS TRES COSAS, no quien pueda las tres.
 * La sección agrupa horarios, catálogo y bloqueos, y el motor los distingue en
 * tres permisos. Exigir los tres para pasar la puerta haría imposible el caso
 * que el ADR-0007 pide: un rol que solo bloquea espacios. Cada panel de dentro
 * comprueba el suyo.
 *
 * NO AUTORIZA NADA. Es para no ofrecer lo que va a rebotar. Quien autoriza son
 * las funciones de PostgreSQL, y la ruta del API vuelve a comprobar antes.
 */

import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { Estado } from '../componentes/Estado'
import type { UsuarioRegistrado } from '../api/perfil'
import { Protegida } from './Protegida'

/**
 * Los tres permisos que abren la sección de administración.
 *
 * Son los mismos que exigen las rutas de `/api/admin` y las funciones del
 * motor. La lista vive aquí y no se deriva de nada porque el frontend no
 * conoce el catálogo: lo que sí se comprueba es que coincida, en
 * `apps/api/pruebas/permisos.test.ts`.
 */
export const PERMISOS_DE_ADMINISTRACION = [
  'administrar_horarios',
  'administrar_espacios',
  'bloquear_espacios',
  // Abre la sección de roles. Sin él, quien SOLO administre permisos no podría
  // entrar a la pantalla que los edita — un rol imposible de usar.
  'administrar_permisos',
  // Y este abre la de docentes, por la misma razón.
  'administrar_asignaciones',
  // El de la migración 030. Es el único de la lista que NO cambia nada: abre el
  // panel de consulta de reservaciones, y es lo que hace útil al rol
  // `consulta`, que hasta entonces no tenía ni un permiso.
  'ver_reservaciones',
] as const

/** Si tiene ALGUNO, la sección tiene algo que ofrecerle. */
export function puedeAdministrar(usuario: UsuarioRegistrado): boolean {
  return PERMISOS_DE_ADMINISTRACION.some((p) => usuario.permisos.includes(p))
}

/** Para que cada panel esconda lo suyo sin repetir el `includes`. */
export function tienePermiso(usuario: UsuarioRegistrado, permiso: string): boolean {
  return usuario.permisos.includes(permiso)
}

interface PropiedadesSoloAdministrador {
  children: (usuario: UsuarioRegistrado) => ReactNode
}

export function SoloAdministrador({ children }: PropiedadesSoloAdministrador) {
  return (
    <Protegida permite={['registrado']}>
      {(perfil) =>
        puedeAdministrar(perfil.usuario) ? (
          <>{children(perfil.usuario)}</>
        ) : (
          <SinPermiso rol={perfil.usuario.rol} />
        )
      }
    </Protegida>
  )
}

/**
 * Lo que ve quien no administra. No es una pantalla de error: no hizo nada mal,
 * simplemente esta sección no es para su cuenta. Se le dice qué es, con qué rol
 * entró y por dónde seguir.
 */
function SinPermiso({ rol }: { rol: string }) {
  return (
    <Estado
      tono="aviso"
      titulo="Esta sección es para administración."
      detalle={
        <>
          Aquí se cargan los horarios de la escuela, los días sin servicio y el
          catálogo de espacios. Tu cuenta tiene el rol <code>{rol}</code>, que
          hoy no incluye ninguno de esos permisos. Quien administre el sistema
          puede otorgártelos desde la propia aplicación, sin tocar el
          servidor.
        </>
      }
      enVivo
    >
      <span className="acciones">
        <Link to="/" className="boton">
          Ir al inicio
        </Link>
        <Link to="/mi-cuenta" className="boton">
          Ver mi cuenta
        </Link>
      </span>
    </Estado>
  )
}
